/**
 * Rocket PIC Panel
 * Manages the PIC panel functionality including automatic unlocking
 */

class RocketPicPanel {
  constructor() {
    this.calendar = document.querySelector('.rocket-pic');
    if (!this.calendar) return;

    this.days = this.calendar.querySelectorAll('.pic-day');
    this.init();
  }

  init() {
    this.checkUnlockStatus();
    this.attachEventListeners();
    this.attachFilterListeners();
    this.attachPopupListeners();
    
    // Check unlock status every minute
    setInterval(() => this.checkUnlockStatus(), 60000);
  }

  /**
   * Attach popup listeners to days with long description
   */
  attachPopupListeners() {
    this.days.forEach(day => {
      if (day.dataset.hasPopup === 'true') {
        day.style.cursor = 'pointer';
        day.addEventListener('click', (e) => {
          // Don't open popup if clicking on a button
          if (e.target.closest('button') || e.target.closest('a')) {
            return;
          }
          this.openPopup(day);
        });
      }
    });
  }

  /**
   * Open popup modal for a specific day
   */
  openPopup(day) {
    const modal = document.getElementById('PopupModal-' + this.calendar.closest('[data-section-id]').dataset.sectionId);
    if (modal && modal.show) {
      modal.show(day);
    }
  }

  /**
   * Attach event listeners to filter tabs
   */
  attachFilterListeners() {
    const tabs = this.calendar.querySelectorAll('.pic-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        this.onTabClick(e.currentTarget);
      });
    });
  }

  /**
   * Handle tab click for filtering
   */
  onTabClick(tab) {
    const type = tab.dataset.tab;
    
    // Remove active class from all tabs
    this.calendar.querySelectorAll('.pic-tab').forEach(t => {
      t.classList.remove('active');
    });
    
    // Add active class to clicked tab
    tab.classList.add('active');
    
    // Filter benefits
    if (type === 'todo') {
      // Show all
      this.days.forEach(day => {
        day.classList.remove('filtered');
      });
    } else {
      // Show only matching type(s)
      this.days.forEach(day => {
        const dayTypes = day.dataset.types || '';
        if (dayTypes.includes(type)) {
          day.classList.remove('filtered');
        } else {
          day.classList.add('filtered');
        }
      });
    }
  }

  /**
   * Check if any days should be unlocked based on current date
   */
  checkUnlockStatus() {
    const now = new Date();
    const currentDate = this.formatDate(now);

    this.days.forEach(day => {
      const startDate = day.dataset.startDate;
      const endDate = day.dataset.endDate;
      if (!startDate) return;

      const isCurrentlyUnlocked = day.classList.contains('unlocked');
      const isCurrentlyLocked = day.classList.contains('locked');
      
      // Check if current date is within the valid range
      let isValidNow = false;
      if (currentDate >= startDate) {
        if (!endDate || endDate === '') {
          // No end date means unlimited
          isValidNow = true;
        } else if (currentDate <= endDate) {
          // Within date range
          isValidNow = true;
        }
      }

      // Check if day should be unlocked
      if (isValidNow && isCurrentlyLocked) {
        this.unlockDay(day);
      }
      // Check if day should be locked (outside valid range)
      else if (!isValidNow && isCurrentlyUnlocked) {
        this.lockDay(day);
      }

      // Add special class for days available today
      if (isValidNow) {
        day.classList.add('available-today');
      } else {
        day.classList.remove('available-today');
      }
    });
  }

  /**
   * Unlock a specific day with animation
   */
  unlockDay(dayElement) {
    // Add unlocking animation
    dayElement.classList.add('just-unlocked');
    
    setTimeout(() => {
      // Remove locked class and add unlocked
      dayElement.classList.remove('locked');
      dayElement.classList.add('unlocked');

      // Toggle images
      const lockedImage = dayElement.querySelector('.locked-image');
      const unlockedImage = dayElement.querySelector('.unlocked-image');
      const info = dayElement.querySelector('.pic-day-info');
      const lockedMessage = dayElement.querySelector('.pic-day-locked-message');

      if (lockedImage) lockedImage.style.display = 'none';
      if (unlockedImage) unlockedImage.style.display = 'block';
      if (info) info.style.display = 'block';
      if (lockedMessage) lockedMessage.style.display = 'none';

      // Remove animation class
      setTimeout(() => {
        dayElement.classList.remove('just-unlocked');
      }, 600);

      // Trigger custom event
      this.triggerUnlockEvent(dayElement);
    }, 100);
  }

  /**
   * Lock a specific day (when it's no longer valid)
   */
  lockDay(dayElement) {
    // Remove unlocked class and add locked
    dayElement.classList.remove('unlocked');
    dayElement.classList.add('locked');

    // Toggle images
    const lockedImage = dayElement.querySelector('.locked-image');
    const unlockedImage = dayElement.querySelector('.unlocked-image');
    const info = dayElement.querySelector('.pic-day-info');
    const lockedMessage = dayElement.querySelector('.pic-day-locked-message');

    if (lockedImage) lockedImage.style.display = 'block';
    if (unlockedImage) unlockedImage.style.display = 'none';
    if (info) info.style.display = 'none';
    if (lockedMessage) lockedMessage.style.display = 'block';

    console.log('Day locked: no longer valid');
  }

  /**
   * Attach event listeners to calendar days
   */
  attachEventListeners() {
    this.days.forEach(day => {
      day.addEventListener('click', (e) => {
        // Only handle click if day is unlocked
        if (!day.classList.contains('unlocked')) {
          this.showLockedMessage(day);
          return;
        }

        // Don't do anything if clicking on buttons
        if (e.target.closest('.pic-btn')) {
          return;
        }

        // Optional: Show modal or expanded view
        this.showDayDetails(day);
      });
    });
  }

  /**
   * Show locked message when clicking on locked day
   */
  showLockedMessage(dayElement) {
    const startDate = dayElement.dataset.startDate;
    const endDate = dayElement.dataset.endDate;
    if (!startDate) return;

    const startDateObj = new Date(startDate + 'T00:00:00');
    const formattedStartDate = startDateObj.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    let message = `Disponible desde el ${formattedStartDate}`;
    if (endDate && endDate !== '') {
      const endDateObj = new Date(endDate + 'T00:00:00');
      const formattedEndDate = endDateObj.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      message += ` hasta el ${formattedEndDate}`;
    }

    console.log(message);

    // Shake animation
    dayElement.style.animation = 'none';
    setTimeout(() => {
      dayElement.style.animation = 'shake 0.5s ease';
    }, 10);

    setTimeout(() => {
      dayElement.style.animation = '';
    }, 500);
  }

  /**
   * Show day details (optional modal)
   */
  showDayDetails(dayElement) {
    const dayNumber = dayElement.dataset.day;
    const type = dayElement.dataset.type;
    
    console.log('Day clicked:', {
      day: dayNumber,
      type: type,
      rewardId: dayElement.dataset.rewardId,
      ruleId: dayElement.dataset.ruleId,
      productHandle: dayElement.dataset.productHandle
    });

    // You can implement a modal here if needed
    // For now, it just logs the information
  }

  /**
   * Trigger custom unlock event for analytics or other integrations
   */
  triggerUnlockEvent(dayElement) {
    const event = new CustomEvent('picDayUnlocked', {
      detail: {
        day: dayElement.dataset.day,
        type: dayElement.dataset.type,
        startDate: dayElement.dataset.startDate,
        endDate: dayElement.dataset.endDate
      }
    });
    
    document.dispatchEvent(event);

    // Google Analytics tracking (if available)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'pic_day_unlocked', {
        'day_number': dayElement.dataset.day,
        'content_type': dayElement.dataset.type
      });
    }
  }

  /**
   * Format date to YYYY-MM-DD
   */
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

// Add required CSS animations
const stylesEl = document.createElement('style');
stylesEl.textContent = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
  }
  
  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes slideOutRight {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(stylesEl);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RocketPicPanel();
    initPicRewardProgress();
  });
} else {
  new RocketPicPanel();
  initPicRewardProgress();
}

/**
 * Populate reward progress bars from LoyaltyLion data.
 * Polls until loyaltylion SDK exposes program.rewards + customer points,
 * AND the hidden LoyaltyLion widget has rendered (for tier-specific costs).
 */
function initPicRewardProgress() {
  var attempts = 0;
  var interval = setInterval(function() {
    attempts++;
    var sdkReady = window.loyaltylion && window.loyaltylion.program && window.loyaltylion.program.rewards && window.loyaltylion.customer;
    var domReady = document.querySelector('[data-lion-rewards-list] .lion-reward-item');

    if (sdkReady && domReady) {
      clearInterval(interval);
      renderPicRewardBars();
    } else if (sdkReady && attempts >= 30) {
      // SDK ready but DOM widget never rendered — proceed with SDK-only fallback
      clearInterval(interval);
      console.warn('[PIC] LoyaltyLion DOM widget not found after 15s, using SDK point_cost (may not be tier-specific)');
      renderPicRewardBars();
    } else if (attempts >= 60) {
      clearInterval(interval);
    }
  }, 500);
}

/**
 * Read the tier-adjusted reward cost from the hidden LoyaltyLion widget DOM.
 * LoyaltyLion renders .lion-reward-item__cost .value with the correct per-tier cost.
 */
function getRewardCostFromDOM(rewardId) {
  var lionItem = document.querySelector('.lion-reward-item[data-reward-id="' + rewardId + '"]');
  if (!lionItem) return null;

  // Primary: .lion-reward-item__cost .value (seen in LL widget structure)
  var costEl = lionItem.querySelector('.lion-reward-item__cost .value');
  if (costEl) {
    var match = costEl.textContent.trim().match(/([\d.,]+)/);
    if (match) return parseInt(match[1].replace(/[.,]/g, ''), 10);
  }

  // Fallback: .lion-reward-item__points .value
  var pointsEl = lionItem.querySelector('.lion-reward-item__points .value');
  if (pointsEl) {
    var match2 = pointsEl.textContent.trim().match(/([\d.,]+)/);
    if (match2) return parseInt(match2[1].replace(/[.,]/g, ''), 10);
  }

  // Fallback: any element with "cost" or "points" in its class
  var generic = lionItem.querySelector('[class*="cost"] .value, [class*="points"] .value');
  if (generic) {
    var match3 = generic.textContent.trim().match(/([\d.,]+)/);
    if (match3) return parseInt(match3[1].replace(/[.,]/g, ''), 10);
  }

  return null;
}

function renderPicRewardBars() {
  var userPoints = window.loyaltylion.customer.pointsRedeemable;
  if (isNaN(userPoints)) return;

  var rewards = window.loyaltylion.program.rewards;
  var rewardMap = {};
  rewards.forEach(function(r) { rewardMap[String(r.id)] = r; });

  document.querySelectorAll('.pic-reward-progress[data-progress-for]').forEach(function(el) {
    var rewardId = el.dataset.progressFor;
    var reward = rewardMap[rewardId];
    if (!reward) return;

    // 1. Try reading the tier-adjusted cost from LoyaltyLion's rendered DOM widget
    var cost = getRewardCostFromDOM(rewardId);

    // 2. Fallback to SDK point_cost (base cost, NOT tier-specific)
    if (!cost || cost <= 0) {
      cost = reward.point_cost;
      console.warn('[PIC] Reward ' + rewardId + ': could not read tier cost from DOM, using SDK point_cost=' + cost);
    } else {
      console.log('[PIC] Reward ' + rewardId + ': tier-adjusted cost from DOM = ' + cost);
    }

    var pct = Math.min((userPoints / cost) * 100, 100);

    el.querySelector('.pic-reward-progress__bar-inner').style.width = pct + '%';
    el.querySelector('.pic-reward-progress__target').textContent = cost + ' skulls';

    if (userPoints >= cost) {
      el.querySelector('.pic-reward-progress__label').textContent = '\u00a1Puedes canjearlo!';
      el.classList.add('pic-reward-progress--redeemable');
    } else {
      el.querySelector('.pic-reward-progress__label').textContent = 'Te faltan ' + (cost - userPoints) + ' skulls';
    }

    el.classList.remove('hidden');
  });
}

// LoyaltyLion Integration Functions
let _redeemInProgress = false;

window.redeemLoyaltyRewardPic = function(rewardId) {
  if (_redeemInProgress) {
    console.log('Redemption already in progress, ignoring click');
    return;
  }

  const normalizedRewardId = String(rewardId || '').trim();
  if (!normalizedRewardId) {
    console.error('[PIC] Empty reward id');
    showNotification('Reward ID vacío o inválido.', 'error');
    return;
  }

  console.log('[PIC] Redeeming reward:', normalizedRewardId);

  const lionItems = Array.from(document.querySelectorAll('.lion-reward-item'));
  const lionItem = lionItems.find((item) => String(item.dataset.rewardId || '').trim() === normalizedRewardId);
  const realRedeemButton = lionItem ? lionItem.querySelector('.lion-reward-item__redeem-button') : null;

  if (realRedeemButton) {
    _redeemInProgress = true;
    realRedeemButton.click();
    _watchRedemptionResult(normalizedRewardId);
  } else {
    console.error('[PIC] Real redeem button not found for reward:', normalizedRewardId);
    showNotification('No se encontró el reward en LoyaltyLion.', 'error');
  }

  if (typeof gtag !== 'undefined') {
    gtag('event', 'pic_reward_click', {
      reward_id: normalizedRewardId
    });
  }
};

/**
 * Watch for the LoyaltyLion redemption result.
 * If the modal disappears (success) or stays stuck loading (error/timeout), 
 * show appropriate feedback and reset the lock.
 */
function _watchRedemptionResult(rewardId) {
  var checkCount = 0;
  var maxChecks = 60; // 30 seconds max wait (60 * 500ms)
  var modalWasOpen = false;
  
  var interval = setInterval(function() {
    checkCount++;
    var modal = document.querySelector('.lion-modal-and-screen');
    
    if (modal) {
      modalWasOpen = true;
    }
    
    // If modal was open and now closed = user cancelled or LoyaltyLion finished
    if (modalWasOpen && !modal) {
      clearInterval(interval);
      _redeemInProgress = false;
      // Refresh the cart drawer after redemption
      updateCartDrawer();
      return;
    }
    
    // Timeout: if after 30s the modal is still open/loading, show error
    if (checkCount >= maxChecks) {
      clearInterval(interval);
      _redeemInProgress = false;
      showNotification('El canje no se pudo completar. Por favor, inténtalo más tarde o contacta con soporte.', 'error');
      
      // Try to close the stuck LoyaltyLion modal
      if (modal) {
        var closeBtn = modal.querySelector('.lion-modal__close, [data-lion-close]');
        if (closeBtn) closeBtn.click();
      }
    }
  }, 500);
}



window.applyLoyaltyRule = function(ruleId) {
  console.log('Applying rule:', ruleId);
  
  // Show loading state
  const loadingOverlay = createLoadingOverlay();
  document.body.appendChild(loadingOverlay);

  // Get customer info from data attributes
  const calendarEl = document.querySelector('.rocket-pic');
  const customerEmail = calendarEl ? calendarEl.dataset.customerEmail : null;
  const customerId = calendarEl ? calendarEl.dataset.customerId : null;
  const isLoggedIn = calendarEl ? calendarEl.dataset.customerLogged === 'true' : false;
  
  if (!isLoggedIn || !customerEmail) {
    loadingOverlay.remove();
    alert('Debes iniciar sesión para aplicar esta regla.');
    return;
  }
  
  // Check if this rule was already applied today by this user
  const today = new Date().toISOString().split('T')[0];
  const appliedKey = `loyalty_rule_${ruleId}_${customerId}_${today}`;
  
  if (localStorage.getItem(appliedKey)) {
    loadingOverlay.remove();
    showNotification('Ya has aplicado esta regla hoy. Vuelve mañana para más recompensas.', 'error');
    return;
  }
  
  // Call Padre middleware to track LoyaltyLion activity
  fetch('/tools/padre/lty-ce/loyalty-track-activity', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({
      rule_id: ruleId,
      customer_id: customerId,
      customer_email: customerEmail,
      date: new Date().toISOString(),
      merchant_id: `pic_${ruleId}_${Date.now()}`
    })
  })
  .then(response => {
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers.get('content-type'));
    
    // Check if response is ok (200-299)
    if (!response.ok) {
      // Try to get error message from response
      return response.text().then(text => {
        console.error('Error response:', text);
        throw new Error(`Error ${response.status}: El endpoint respondió con un error. Verifica el backend de Padre.`);
      });
    }
    
    // Check if response is JSON
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return response.json();
    } else {
      // Not JSON, log the response
      return response.text().then(text => {
        console.error('Response is not JSON:', text.substring(0, 200));
        throw new Error('La respuesta no es JSON. El endpoint de Padre debe devolver JSON con {success: true/false}');
      });
    }
  })
  .then(data => {
    loadingOverlay.remove();
    console.log('Success response:', data);
    
    if (data.success) {
      // Success
      showNotification('¡Regla aplicada correctamente! Has ganado ' + getPointsForRule(ruleId) + '.', 'success');
      
      // Mark as applied
      localStorage.setItem(appliedKey, Date.now().toString());
      
      // Refresh LoyaltyLion UI
      setTimeout(() => {
        if (typeof loyaltylion !== 'undefined' && loyaltylion.ui) {
          if (typeof loyaltylion.ui.refresh === 'function') {
            loyaltylion.ui.refresh();
          }
          if (typeof loyaltylion.ui.open === 'function') {
            loyaltylion.ui.open();
          }
        }
      }, 1000);
    } else {
      showNotification('Error al aplicar la regla: ' + (data.error || data.message || 'Error desconocido'), 'error');
    }
  })
  .catch(error => {
    loadingOverlay.remove();
    console.error('Error applying rule:', error);
    showNotification(error.message || 'Error al aplicar la regla. Por favor, inténtalo más tarde.', 'error');
  });

  // Track event
  if (typeof gtag !== 'undefined') {
    gtag('event', 'pic_rule_click', {
      'rule_id': ruleId
    });
  }
};

// Helper function to get points for a rule (customize based on your rules)
function getPointsForRule(ruleId) {
  const pointsMap = {
    'puntos_ac': '500 skulls',
    // Add more rule mappings here
  };
  return pointsMap[ruleId] || 'bonus';
}



/**
 * Create loading overlay
 */
function createLoadingOverlay() {
  const overlay = document.createElement('div');
  overlay.className = 'pic-loading';
  overlay.innerHTML = '<div class="loading-spinner"></div>';
  return overlay;
}

/**
 * Show notification message
 */
function showNotification(message, type = 'info') {
  const notification = document.createElement('div');
  const colors = {
    info: '#007bff',
    success: '#28a745',
    error: '#dc3545'
  };
  
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 15px 25px;
    background: ${colors[type] || colors.info};
    color: white;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 10000;
    animation: slideInRight 0.3s ease;
  `;
  notification.textContent = message;
  
  document.body.appendChild(notification);
  
  setTimeout(() => {
    notification.style.animation = 'slideOutRight 0.3s ease';
    setTimeout(() => notification.remove(), 300);
  }, 3000);
}



// Listen for pic day unlocked events
document.addEventListener('picDayUnlocked', (e) => {
  showNotification(`¡Día ${e.detail.day} desbloqueado!`, 'success');
});

// Custom Elements for Modal functionality
class PicDayDetail extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('pic-day-detail', PicDayDetail);

class PicDetailList extends HTMLElement {
  constructor() {
    super();
    this.initSwiper();
  }
  
  initSwiper() {
  const initSwiperWhenReady = () => {
    if (typeof Swiper !== 'undefined') {
      // Remove tier-hidden slides from modal swiper to avoid index drift
      this.querySelectorAll('.swiper-slide.hidden').forEach((slide) => slide.remove());

      this.swiper = new Swiper(this.querySelector('.swiper'), {
        slidesPerView: 1,
        spaceBetween: 10,
      });
    } else {
      setTimeout(initSwiperWhenReady, 100);
    }
  };
  initSwiperWhenReady();
}
}

customElements.define('pic-detail-list', PicDetailList);

class PicDialog extends HTMLElement {
  constructor() {
    super();
    
    const modalClose = this.querySelector('[id^="ModalClose-"]');
    if (modalClose) {
      modalClose.addEventListener('click', this.hide.bind(this));
    }
    
    // Close on overlay click
    this.addEventListener('click', (e) => {
      if (e.target === this) {
        this.hide();
      }
    });
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) {
        this.hide();
      }
    });
  }
  
show(opener) {
  this.setAttribute('open', '');
  this.classList.add('active');
  document.body.style.overflow = 'hidden';

  if (!opener) return;

  const detailList = this.querySelector('pic-detail-list');
  if (!detailList || !detailList.swiper) return;

  const blockId = (opener.dataset.blockId || '').trim();
  if (!blockId) {
    console.error('[PIC] opener without blockId');
    return;
  }

  requestAnimationFrame(() => {
    const swiper = detailList.swiper;
    swiper.update();

    const slides = Array.from(swiper.slides || []);
    const targetIndex = slides.findIndex((slide) => {
      const slideBlockId = (slide.dataset.blockId || '').trim();
      if (slideBlockId === blockId) return true;

      const detail = slide.querySelector('pic-day-detail');
      const detailBlockId = detail ? (detail.dataset.blockId || '').trim() : '';
      return detailBlockId === blockId;
    });

    if (targetIndex >= 0) {
      swiper.slideTo(targetIndex, 0, false);
    } else {
      console.error('[PIC] No matching modal slide for blockId:', blockId);
    }
  });
}
  
  hide() {
    this.removeAttribute('open');
    this.classList.remove('active');
    document.body.style.overflow = '';
  }
}

customElements.define('pic-dialog', PicDialog);

// =============================================
// LoyaltyLion Ajax Cart Integration
// Listens for lion cart events to refresh the cart drawer
// Reference: https://github.com/loyaltylion-docs/shopify-incart-rewards-reference#usage-with-ajax-carts
// =============================================

/**
 * Update cart drawer by fetching fresh section HTML, same approach as product-form.js
 */
function updateCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) {
    console.error('[PIC] Cart drawer element not found');
    return;
  }

  const formData = new FormData();
  formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id));
  formData.append('sections_url', window.location.pathname);
  cartDrawer.setActiveElement(document.activeElement);

  fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: formData
  })
  .then((response) => response.json())
  .then((response) => {
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'pic-panel-loyalty',
        action: 'cart-changed'
      });
    }
    cartDrawer.renderContents(response);
    console.log('[PIC] Cart drawer updated successfully');
  })
  .catch((error) => {
    console.error('[PIC] Error updating cart drawer:', error);
  });
}

/**
 * Notify LoyaltyLion of cart state changes made by our code
 * (so LoyaltyLion can recalculate in-cart rewards)
 */
function notifyLionCartState() {
  fetch('/cart.js')
    .then((response) => response.json())
    .then((cart) => {
      if (typeof lion !== 'undefined' && typeof lion.setCartState === 'function') {
        lion.setCartState(cart);
        console.log('[PIC] Notified LoyaltyLion of cart state change');
      }
    })
    .catch((error) => {
      console.error('[PIC] Error fetching cart for lion.setCartState:', error);
    });
}

/**
 * Initialize LoyaltyLion cart event listeners.
 * When LoyaltyLion adds or removes reward items from the cart,
 * we refresh the cart drawer so the user sees the updated cart.
 */
function initLionCartListeners() {
  if (typeof lion === 'undefined') return;

  lion.on('cart.changed', function() {
    console.log('[PIC] LoyaltyLion cart.changed event — refreshing cart drawer');
    updateCartDrawer();
  });

  lion.on('cart.addedItem', function(data) {
    console.log('[PIC] LoyaltyLion added cart item:', data.item);
  });

  lion.on('cart.removedItems', function(data) {
    console.log('[PIC] LoyaltyLion removed cart items:', data.items);
  });

  console.log('[PIC] LoyaltyLion cart listeners initialized');
}

// Initialize lion listeners when the SDK is ready
(function() {
  if (typeof lion !== 'undefined') {
    initLionCartListeners();
  } else {
    // LoyaltyLion SDK may load asynchronously; wait for it
    var lionCheckCount = 0;
    var lionCheckInterval = setInterval(function() {
      lionCheckCount++;
      if (typeof lion !== 'undefined') {
        clearInterval(lionCheckInterval);
        initLionCartListeners();
      } else if (lionCheckCount >= 60) {
        // Stop trying after ~30s
        clearInterval(lionCheckInterval);
        console.warn('[PIC] LoyaltyLion SDK not detected after 30s');
      }
    }, 500);
  }
})();

// Export for module usage (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RocketPicPanel;
}
